import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import { FaMobileAlt, FaUser, FaMoneyBillWave, FaInfoCircle } from 'react-icons/fa';
import '../styles/HubtelReceive.css';

const API_BASE_URL = 'https://whale-app-fk7fs.ondigitalocean.app';

function HubtelReceive() {
  const [formData, setFormData] = useState({
    amount: '',
    customerName: '',
    customerMsisdn: '',
    description: '',
    channel: 'mtn-gh'
  });
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchNetworks();
  }, []);

  const fetchNetworks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payment/networks`);
      setNetworks(response.data.data);
    } catch (error) {
      toast.error('Failed to load networks');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formatPhoneNumber = (phone) => {
    // Ensure phone starts with 233
    let formatted = phone.replace(/\D/g, '');
    if (formatted.startsWith('0')) {
      formatted = '233' + formatted.substring(1);
    }
    if (formatted.length === 9) {
      formatted = '233' + formatted;
    }
    return formatted.substring(0, 12); // Max 12 digits
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) < 0.1) {
      toast.error('Please enter a valid amount (minimum 0.1)');
      return;
    }

    if (!formData.customerName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (!formData.customerMsisdn.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    const formattedPhone = formatPhoneNumber(formData.customerMsisdn);
    if (formattedPhone.length !== 12) {
      toast.error('Please enter a valid Ghanaian phone number');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        customerMsisdn: formattedPhone
      };

      const response = await axios.post(`${API_BASE_URL}/payment/initiate`, payload);
      
      if (response.data.success) {
        setCurrentTransaction(response.data.data.transactionId);
        toast.success('Payment initiated! Check your phone for approval');
        
        // Start polling for status
        pollPaymentStatus(response.data.data.transactionId);
      } else {
        toast.error(response.data.message || 'Payment initiation failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (transactionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/payment/status/${transactionId}`);
        
        if (response.data.success) {
          const statusData = response.data.data;
          
          if (statusData.status === 'Completed') {
            setPaymentStatus('success');
            toast.success('Payment completed successfully!');
            clearInterval(pollInterval);
            resetForm();
          } else if (statusData.status === 'Failed' || statusData.status === 'Cancelled') {
            setPaymentStatus('failed');
            toast.error(`Payment ${statusData.status.toLowerCase()}`);
            clearInterval(pollInterval);
          }
          // If still pending, continue polling
        }
      } catch (error) {
        console.error('Status check error:', error);
        clearInterval(pollInterval);
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (paymentStatus === null) {
        toast.error('Payment timeout. Please try again.');
      }
    }, 300000);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      customerName: '',
      customerMsisdn: '',
      description: '',
      channel: 'mtn-gh'
    });
    setTimeout(() => {
      setCurrentTransaction(null);
      setPaymentStatus(null);
    }, 5000);
  };

  const getNetworkIcon = (networkId) => {
    const network = networks.find(n => n.id === networkId);
    return network ? network.icon : 'M';
  };

  return (
    <div className="app">
      <Toaster position="top-right" />
      
      <div className="container">
        <header className="header">
          <h1><FaMobileAlt /> Hubtel Mobile Money Payments</h1>
          <p>Secure and fast mobile money transactions</p>
        </header>

        <div className="payment-card">
          <div className="card-header">
            <h2>Make a Payment</h2>
            <div className="network-icons">
              {networks.map(network => (
                <div 
                  key={network.id} 
                  className={`network-icon ${formData.channel === network.id ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, channel: network.id})}
                >
                  {network.icon}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-group">
              <label htmlFor="amount">
                <FaMoneyBillWave /> Amount (GHS)
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0.1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="customerName">
                <FaUser /> Full Name
              </label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="customerMsisdn">
                <FaMobileAlt /> Phone Number
              </label>
              <input
                type="tel"
                id="customerMsisdn"
                name="customerMsisdn"
                value={formData.customerMsisdn}
                onChange={handleChange}
                placeholder="e.g., 233XXXXXXXXX or 0XXXXXXXXX"
                required
              />
              <small className="hint">Enter your Ghanaian mobile money number</small>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                <FaInfoCircle /> Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Payment description..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Select Network</label>
              <div className="network-buttons">
                {networks.map(network => (
                  <button
                    key={network.id}
                    type="button"
                    className={`network-btn ${formData.channel === network.id ? 'selected' : ''}`}
                    onClick={() => setFormData({...formData, channel: network.id})}
                  >
                    {network.name}
                  </button>
                ))}
              </div>
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Processing...' : `Pay GHS ${formData.amount || '0.00'}`}
            </button>

            {currentTransaction && (
              <div className="transaction-info">
                <p>Transaction ID: <strong>{currentTransaction}</strong></p>
                <p className="instruction">
                  Please approve the payment request on your phone
                </p>
                <div className="status-indicator">
                  <div className={`status-dot ${paymentStatus === 'success' ? 'success' : paymentStatus === 'failed' ? 'failed' : 'pending'}`}></div>
                  <span>
                    {paymentStatus === 'success' ? 'Payment Successful' : 
                     paymentStatus === 'failed' ? 'Payment Failed' : 
                     'Waiting for Approval...'}
                  </span>
                </div>
              </div>
            )}
          </form>

          <div className="instructions">
            <h3>How to Pay:</h3>
            <ol>
              <li>Enter amount and your details</li>
              <li>Select your mobile money network</li>
              <li>Click "Pay" to initiate payment</li>
              <li>Approve the payment request on your phone</li>
              <li>Wait for confirmation</li>
            </ol>
            <div className="security-note">
              🔒 Your payment is secured by HuntexLotto. We do not store your financial details.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HubtelReceive;